import { useState, useCallback, useRef } from 'react';

const STORAGE_KEY = 'tradrin-ai-config';
const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8000/api'
  : '/api';

function loadConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function useAIJournal(analyticsData) {
  const saved = loadConfig();
  const [provider, setProvider] = useState(saved?.provider || 'claude');
  const [model, setModel] = useState(saved?.model || 'claude-sonnet-4-6');
  const [apiKey, setApiKey] = useState(saved?.apiKey || '');
  const [baseUrl, setBaseUrl] = useState(saved?.baseUrl || '');
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const isConfigured = !!(apiKey && model);

  const sendMessage = useCallback(async (userText) => {
    if (!userText.trim() || !apiKey || !model) return;
    setError(null);

    const userMsg = { role: 'user', content: userText };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);

    const abortController = new AbortController();
    abortRef.current = abortController;
    setStreaming(true);
    const assistantIdx = newMessages.length;
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const token = localStorage.getItem('token');
      const body = {
        provider, model, api_key: apiKey,
        messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        analytics_data: analyticsData || {},
      };
      if (provider === 'custom' && baseUrl) body.base_url = baseUrl;

      const response = await fetch(`${API_BASE}/ai/journal/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              accumulated += data.text;
              const snapshot = accumulated;
              setMessages(prev => {
                const updated = [...prev];
                updated[assistantIdx] = { role: 'assistant', content: snapshot };
                return updated;
              });
            } else if (data.type === 'error') {
              setError(data.text);
            }
          } catch { /* skip */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message || 'Connection failed.');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [messages, provider, model, apiKey, baseUrl, analyticsData]);

  const clearChat = useCallback(() => { setMessages([]); setError(null); }, []);
  const stopStreaming = useCallback(() => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    setStreaming(false);
  }, []);

  return {
    provider, model, apiKey, baseUrl,
    setProvider, setModel, setApiKey, setBaseUrl,
    messages, streaming, error,
    sendMessage, clearChat, stopStreaming, isConfigured,
  };
}
